import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money } from '../lib/format';

const EMPTY = { name: '', sku: '', description: '', unit_price: 0, income_account_id: '', taxable: true, is_active: true };

export default function Items() {
  const { activeId } = useBusiness();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [incomeAccounts, setIncomeAccounts] = useState([]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api(`/items?business_id=${activeId}`)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setRows([]);
        if (e?.status && e.status !== 404) setError('Could not load items. Check the API connection.');
      })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api(`/accounts?business_id=${activeId}&type=income`)
      .then((d) => setIncomeAccounts(Array.isArray(d) ? d : []))
      .catch(() => setIncomeAccounts([]));
  }, [activeId]);

  function openNew() {
    setEditingId(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }
  async function openEdit(row) {
    setEditingId(row.id);
    setFormError(null);
    setOpen(true);
    try {
      const it = await api(`/items/${row.id}`);
      setForm({
        name: it.name || '', sku: it.sku || '', description: it.description || '',
        unit_price: Number(it.unit_price) || 0, income_account_id: it.income_account_id || '',
        taxable: it.taxable !== false, is_active: it.is_active !== false,
      });
    } catch { setForm(EMPTY); }
  }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError(null);
    const body = {
      name: form.name.trim(), sku: form.sku || null, description: form.description || null,
      unit_price: Number(form.unit_price) || 0,
      income_account_id: form.income_account_id || null,
      taxable: form.taxable,
    };
    try {
      if (editingId) {
        await api(`/items/${editingId}`, { method: 'PUT', body: { ...body, is_active: form.is_active } });
      } else {
        await api('/items', { method: 'POST', body: { ...body, business_id: activeId } });
      }
      setOpen(false);
      load();
    } catch (e) {
      setFormError(e?.detail || 'Could not save the item.');
    } finally {
      setSaving(false);
    }
  }

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'sku', header: 'SKU', render: (r) => r.sku || '—' },
    { key: 'unit_price', header: 'Price', render: (r) => <span className="num">{money(r.unit_price)}</span> },
    { key: 'income', header: 'Income account', render: (r) => (r.income_code ? `${r.income_code} · ${r.income_name}` : '—') },
    { key: 'taxable', header: 'Tax', render: (r) => (r.taxable !== false ? 'Yes' : '—') },
    { key: 'is_active', header: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'cancelled'} /> },
  ];

  return (
    <>
      <PageHeader title="Items" actions={can('items.manage') && <button className="btn btn--primary" onClick={openNew}>New item</button>} />
      <section className="panel">
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          onRowClick={openEdit}
          empty="No items yet. Add products or services you bill for."
        />
      </section>

      <Drawer
        open={open}
        title={editingId ? 'Edit item' : 'New item'}
        onClose={() => !saving && setOpen(false)}
        footer={
          <>
            <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create item'}
            </button>
          </>
        }
      >
        {formError && <div className="form-error">{formError}</div>}
        <div className="field">
          <label>Name *</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
        </div>
        <div className="form-grid">
          <div className="field">
            <label>SKU</label>
            <input value={form.sku} onChange={(e) => set('sku', e.target.value)} />
          </div>
          <div className="field">
            <label>Unit price</label>
            <input type="number" step="0.01" value={form.unit_price} onChange={(e) => set('unit_price', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Description</label>
          <textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Income account</label>
            <select value={form.income_account_id} onChange={(e) => set('income_account_id', e.target.value)}>
              <option value="">Default (Service Revenue)</option>
              {incomeAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label className="check" style={{ marginBottom: 9 }}>
              <input type="checkbox" checked={form.taxable} onChange={(e) => set('taxable', e.target.checked)} />
              Taxable
            </label>
          </div>
        </div>
        {editingId && (
          <label className="check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
            Active
          </label>
        )}
      </Drawer>
    </>
  );
}
